/// <reference types="google.maps" />
import { useQuery } from "@tanstack/react-query";
import { APIProvider, AdvancedMarker, AdvancedMarkerAnchorPoint, Map, useMap } from "@vis.gl/react-google-maps";
import { createContext, useContext, useEffect, useRef, type ReactNode, useMemo } from "react";

import { queries } from "@/api";
import { useResolvedTheme } from "@/lib/theme";

/**
 * Google Maps for the pages (F468, decision 091): the platform hands its
 * browser key and map id to signed-in clients (`GET /api/v1/config`), the
 * provider loads Google's script once with it, and every map draws on it
 * with Google's own controls (the wheel and the pinch zoom, the map types,
 * full screen). Without a key the maps say so and the pages go on.
 */
export type Maps = { key: string; mapId: string };

/** `undefined` while the platform's answer is on its way, `null` without a key. */
const MapsContext = createContext<Maps | null | undefined>(undefined);

/**
 * Hands the platform's answer down; Google's own provider sits inside each
 * map's frame, so the pages around a map never remount when the answer
 * arrives (the script is loaded once all the same: the loader is shared).
 */
export function MapsProvider({ children }: { children: ReactNode }) {
  const config = useQuery(queries.config());
  const maps: Maps | null | undefined = config.isPending ? undefined : config.data?.maps ? { key: config.data.maps.key, mapId: config.data.maps.map_id } : null;
  return <MapsContext.Provider value={maps}>{children}</MapsContext.Provider>;
}

export function useMaps(): Maps | null | undefined {
  return useContext(MapsContext);
}

export type LatLon = { lat: number; lon: number };

/**
 * The frame of a map: Google's map in the appearance shown, the wheel
 * zooming with Ctrl alone so the page scrolls past the map without a
 * catch (F509, decision 116: Google's cooperative handling says so on the
 * map), the pinch free, the map types and full screen on, the street view
 * off. Remade when the appearance flips (Google sets its colour scheme at
 * creation alone).
 */
export function MapFrame({ className, testId, center, zoom, scale = false, children }: { className: string; testId: string; center: LatLon; zoom: number; scale?: boolean; children?: ReactNode }) {
  const maps = useMaps();
  const theme = useResolvedTheme();
  if (maps === undefined) return <div className={`${className} bg-paper-3`} data-testid={testId} />;
  if (maps === null) {
    return (
      <div className={`${className} flex items-center justify-center bg-paper-3 p-6 text-center`} data-testid={testId}>
        <p className="muted max-w-md text-sm" data-testid="map-off">
          The map is off on this platform: its Google Maps key is not set. The aircraft and their places are listed all the same.
        </p>
      </div>
    );
  }
  return (
    <div className={className} data-testid={testId}>
      <APIProvider apiKey={maps.key} version="weekly">
        <Map
          key={theme}
          mapId={maps.mapId}
          colorScheme={theme === "dark" ? "DARK" : "LIGHT"}
          defaultCenter={{ lat: center.lat, lng: center.lon }}
          defaultZoom={zoom}
          gestureHandling="cooperative"
          streetViewControl={false}
          mapTypeControl
          fullscreenControl
          zoomControl
          scaleControl={scale}
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          <Veil dark={theme === "dark"} />
          {children}
        </Map>
      </APIProvider>
    </div>
  );
}

/**
 * Google's base dimmed a touch (F474, decision 096): a polygon over the
 * whole world at the bottom of the overlays, so the lines, the dots, the
 * notes and the controls keep their full colour over a quieter base, in
 * full screen too; never clickable.
 */
function Veil({ dark }: { dark: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    const veil = new google.maps.Polygon({
      map,
      paths: [
        { lat: 85, lng: -180 },
        { lat: 85, lng: 0 },
        { lat: 85, lng: 180 },
        { lat: -85, lng: 180 },
        { lat: -85, lng: 0 },
        { lat: -85, lng: -180 },
      ],
      fillColor: dark ? "#000000" : "#0b1220",
      fillOpacity: dark ? 0.3 : 0.14,
      strokeOpacity: 0,
      clickable: false,
      zIndex: 0,
    });
    return () => veil.setMap(null);
  }, [map, dark]);
  return null;
}

/** The view fits these points, refitted when `fitKey` changes (not on every move). */
export function FitBounds({ points, fitKey, maxZoom = 9, padding = 40 }: { points: LatLon[]; fitKey: string; maxZoom?: number; padding?: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map || points.length === 0 || typeof google === "undefined") return;
    if (points.length === 1) {
      map.setCenter({ lat: points[0].lat, lng: points[0].lon });
      map.setZoom(Math.min(maxZoom, 8));
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const p of points) bounds.extend({ lat: p.lat, lng: p.lon });
    map.fitBounds(bounds, padding);
    const once = google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (z !== undefined && z > maxZoom) map.setZoom(maxZoom);
    });
    return () => google.maps.event.removeListener(once);
  }, [map, fitKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** The view pans to a point as it moves (the followed aircraft); `keepInView` pans only when the point leaves the view (the playback). */
export function PanTo({ at, keepInView = false }: { at: LatLon | null; keepInView?: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !at) return;
    const here = { lat: at.lat, lng: at.lon };
    if (keepInView && map.getBounds()?.contains(here)) return;
    map.panTo(here);
  }, [map, at?.lat, at?.lon, keepInView]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * A line on the map (a trail, a route, the line ahead). The Google polyline
 * is made once per map and its path changed in place, a dark casing under it
 * so it reads on any base (F474, decision 096); a hidden span names it to the
 * tests with its point count.
 */
export function Line({ points, color, weight = 2, opacity = 0.9, dashed = false, casing = true, testId = "map-line" }: { points: LatLon[]; color: string; weight?: number; opacity?: number; dashed?: boolean; casing?: boolean; testId?: string }) {
  const map = useMap();
  const line = useRef<google.maps.Polyline | null>(null);
  const under = useRef<google.maps.Polyline | null>(null);
  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    const below = new google.maps.Polyline({ map, zIndex: 1 });
    const made = new google.maps.Polyline({ map, zIndex: 2 });
    under.current = below;
    line.current = made;
    return () => {
      below.setMap(null);
      made.setMap(null);
      under.current = null;
      line.current = null;
    };
  }, [map]);
  useEffect(() => {
    const l = line.current;
    const u = under.current;
    if (!l || !u) return;
    const path = points.map((p) => ({ lat: p.lat, lng: p.lon }));
    l.setOptions({
      path,
      strokeColor: color,
      strokeWeight: weight,
      strokeOpacity: dashed ? 0 : opacity,
      icons: dashed ? [{ icon: { path: "M 0,-1 0,1", strokeOpacity: opacity, strokeColor: color, scale: weight }, offset: "0", repeat: "14px" }] : undefined,
    });
    u.setOptions({
      path,
      strokeColor: "#0b1220",
      strokeWeight: weight + 3,
      strokeOpacity: casing && !dashed ? 0.55 : 0,
    });
  }, [map, points, color, weight, opacity, dashed, casing]);
  return <span hidden data-testid={testId} data-points={points.length} />;
}

/** The track's colours by altitude, as a flight radar draws them (2026-11-03): grey on the ground, then yellow to violet with the height. */
export const ALTITUDE_BANDS: { upTo: number; colour: string; label: string }[] = [
  { upTo: 2000, colour: "#e8c547", label: "under 2,000 ft" },
  { upTo: 5000, colour: "#a4d65e", label: "under 5,000" },
  { upTo: 10000, colour: "#3fcb84", label: "under 10,000" },
  { upTo: 20000, colour: "#2fb5d0", label: "under 20,000" },
  { upTo: 30000, colour: "#1d6ff2", label: "under 30,000" },
  { upTo: Infinity, colour: "#8e5cf0", label: "30,000 and above" },
];
export const GROUND_COLOUR = "#8d97a8";

export function altitudeColour(altFt: number, onGround: boolean): string {
  if (onGround) return GROUND_COLOUR;
  return (ALTITUDE_BANDS.find((b) => altFt < b.upTo) ?? ALTITUDE_BANDS[ALTITUDE_BANDS.length - 1]!).colour;
}

export type TrackPoint = LatLon & { alt_ft: number; on_ground: boolean };

/** The runs of one colour along a track; each run starts where the one before ends, so the line has no gap. */
export function colourRuns(points: TrackPoint[]): { colour: string; points: LatLon[] }[] {
  const runs: { colour: string; points: LatLon[] }[] = [];
  for (const p of points) {
    const colour = altitudeColour(p.alt_ft, p.on_ground);
    const here = { lat: p.lat, lon: p.lon };
    const last = runs[runs.length - 1];
    if (last && last.colour === colour) last.points.push(here);
    else runs.push({ colour, points: last ? [last.points[last.points.length - 1]!, here] : [here] });
  }
  return runs.filter((r) => r.points.length > 1);
}

/** A track drawn in the colours of its altitude: one line per run. */
export function ColourTrail({ points, weight = 2.5, opacity = 0.95, testId = "map-line" }: { points: TrackPoint[]; weight?: number; opacity?: number; testId?: string }) {
  const runs = useMemo(() => colourRuns(points), [points]);
  return (
    <>
      {runs.map((r, k) => (
        <Line key={k} points={r.points} color={r.colour} weight={weight} opacity={opacity} testId={testId} />
      ))}
    </>
  );
}

/** The legend of the colours, over a map's corner. */
export function AltitudeLegend() {
  return (
    <div className="map-legend" aria-label="The track's colours by altitude" data-testid="altitude-legend">
      <span>
        <i style={{ background: GROUND_COLOUR }} /> ground
      </span>
      {ALTITUDE_BANDS.map((b) => (
        <span key={b.label}>
          <i style={{ background: b.colour }} /> {b.label}
        </span>
      ))}
    </div>
  );
}

const PLANE_PATH = "M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z";

/** The aircraft, pointed on its heading, centred on its place. */
/**
 * The plane on the map. `pulse` is the number of the last publication of a
 * flying aircraft (F484, decision 102): a ring swells from the icon each
 * time it changes, one swell per publication, so an aircraft stopped on
 * the ground still reads as live; none for a parked aircraft.
 */
export function Plane({ at, hdg, fill, size = 28, title, pulse = null, onClick, children }: { at: LatLon; hdg: number; fill: string; size?: number; title?: string; pulse?: number | null; onClick?: () => void; children?: ReactNode }) {
  return (
    <AdvancedMarker position={{ lat: at.lat, lng: at.lon }} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} title={title} onClick={onClick} zIndex={2}>
      <span style={{ position: "relative", display: "block", width: size, height: size }}>
        {pulse !== null ? <span key={pulse} className="map-pulse" data-testid="map-pulse" data-at={pulse} aria-hidden="true" /> : null}
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ position: "relative", transform: `rotate(${Math.round(hdg)}deg)`, transformOrigin: "50% 50%" }} fill={fill} stroke="#ffffff" strokeWidth="1.2" aria-hidden="true" data-testid="map-plane">
          <path d={PLANE_PATH} />
        </svg>
      </span>
      {children}
    </AdvancedMarker>
  );
}

/** The zoom a click on an aircraft brings the map to (F509): the airport and its surroundings. */
export const CLOSE_ZOOM = 9;

/** Brings the map onto a point, close (F509): a click on an aircraft. The view keeps a closer zoom it already had. */
export function bringClose(map: google.maps.Map | null, at: LatLon) {
  if (!map) return;
  map.panTo({ lat: at.lat, lng: at.lon });
  const z = map.getZoom() ?? 0;
  if (z < CLOSE_ZOOM) map.setZoom(CLOSE_ZOOM);
}

/**
 * The card of an aircraft on the map (F509): ours, in the page's colours,
 * above the aircraft, with its close; Google's white window is gone.
 */
export function MapCard({ at, title, onClose, children }: { at: LatLon; title: ReactNode; onClose: () => void; children?: ReactNode }) {
  return (
    <AdvancedMarker position={{ lat: at.lat, lng: at.lon }} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} zIndex={3} clickable>
      <div className="map-card" role="dialog" aria-label={typeof title === "string" ? title : "The aircraft"} data-testid="map-popup">
        <div className="map-card-head">
          <strong className="min-w-0 flex-1 truncate">{title}</strong>
          <button type="button" className="map-card-close" onClick={onClose} aria-label="Close" data-testid="map-popup-close">
            ×
          </button>
        </div>
        <div className="text-sm">{children}</div>
      </div>
    </AdvancedMarker>
  );
}

/** A dot on the map: a take-off, a landing, a limit exceeded; big enough to hit, answering a click (F474). */
export function Dot({ at, fill, title, onClick, testId = "map-dot" }: { at: LatLon; fill: string; title?: string; onClick?: () => void; testId?: string }) {
  return (
    <AdvancedMarker position={{ lat: at.lat, lng: at.lon }} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} title={title} onClick={onClick}>
      <span
        style={{ display: "block", width: 16, height: 16, borderRadius: 999, background: fill, border: "2.5px solid #ffffff", boxShadow: "0 0 0 1.5px rgb(11 18 32 / 0.6)", cursor: onClick ? "pointer" : "default" }}
        data-testid={testId}
      />
    </AdvancedMarker>
  );
}
